import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx, Input } from "./ui";

function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return { open, setOpen, ref };
}

// FilterPill: a filter chip next to the search field. Shows the applied
// value; opens a searchable option list; × clears it.
export function FilterPill({ label, value, options, onChange, searchPlaceholder, clearLabel }: {
  label: string; value: string; options: { value: string; label?: string }[]; onChange: (v: string) => void;
  searchPlaceholder: string; clearLabel: string;
}) {
  const { open, setOpen, ref } = usePopover();
  const [q, setQ] = useState("");
  const shown = options.filter((o) => (o.label ?? o.value).toLowerCase().includes(q.toLowerCase()));
  const current = options.find((o) => o.value === value);
  return (
    <div ref={ref} className="relative">
      <div className={cx("inline-flex h-9 items-center rounded-full border text-sm", value ? "border-brand/30 bg-brand-tint text-brand" : "border-line-strong bg-white text-ink-muted hover:bg-subtle hover:text-ink")}>
        <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)} className="inline-flex h-full items-center gap-1 px-3.5">
          {value ? <>{label}: <span className="font-medium">{current?.label ?? value}</span></> : <><span aria-hidden className="text-ink-subtle">+</span> {label}</>}
        </button>
        {value && <button type="button" aria-label={`${clearLabel} ${label}`} onClick={() => onChange("")} className="flex h-full items-center pe-3 ps-0.5 hover:opacity-70">×</button>}
      </div>
      {open && (
        <div className="anim-pop absolute start-0 z-30 mt-1.5 w-64 rounded-xl border border-line bg-white p-2 text-ink shadow-pop">
          {options.length > 8 && <Input autoFocus className="mb-2" placeholder={searchPlaceholder} value={q} onChange={(e) => setQ(e.target.value)} />}
          <ul role="listbox" className="max-h-64 overflow-auto">
            {shown.map((o) => (
              <li key={o.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-subtle">
                  <input type="radio" name={label} checked={value === o.value} onChange={() => { onChange(o.value); setOpen(false); }} />
                  <span className="truncate">{o.label ?? o.value}</span>
                </label>
              </li>
            ))}
            {shown.length === 0 && <li className="px-2 py-1.5 text-sm text-ink-muted">—</li>}
          </ul>
          {value && <button type="button" className="mt-1 w-full rounded-lg px-2 py-1.5 text-start text-sm text-brand hover:bg-subtle" onClick={() => { onChange(""); setOpen(false); }}>{clearLabel}</button>}
        </div>
      )}
    </div>
  );
}

// MenuButton: a button with a popover of choices (row actions, Sort).
// The menu is portaled to <body> with fixed positioning, so a table's
// scroll container or a card's overflow never clips it or grows a
// scrollbar. It lines up with the button's end edge, opens upward when
// there's no room below (placement "top" prefers upward, as in the
// floating bulk-action bar), and follows the button on scroll.
const MENU_W = 224, GAP = 6, MARGIN = 8;

export function MenuButton({ label, children, placement = "bottom" }: { label: ReactNode; children: (close: () => void) => ReactNode; placement?: "bottom" | "top" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; maxHeight: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  const place = useCallback(() => {
    const b = button.current?.getBoundingClientRect();
    if (!b) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    // Gone from view (scrolled away): close rather than float detached.
    if (b.bottom < 0 || b.top > vh) { setOpen(false); return; }
    const rtl = getComputedStyle(button.current!).direction === "rtl";
    const width = Math.min(MENU_W, vw - 2 * MARGIN);
    const left = Math.min(Math.max(rtl ? b.left : b.right - width, MARGIN), vw - width - MARGIN);
    const h = menu.current?.offsetHeight ?? 0;
    const below = vh - b.bottom - GAP - MARGIN, above = b.top - GAP - MARGIN;
    const up = placement === "top" ? above >= Math.min(h, 160) || above > below : h > below && above > below;
    setPos(up ? { bottom: vh - b.top + GAP, left, maxHeight: above } : { top: b.bottom + GAP, left, maxHeight: below });
  }, [placement]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
    // Measured once rendered: place again with the menu's real height.
    const raf = requestAnimationFrame(place);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!button.current?.contains(t) && !menu.current?.contains(t)) setOpen(false);
    };
    // Escape closes the menu only, not a dialog it sits in.
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); button.current?.focus(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", place, true); // capture: any scrolling ancestor
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return (
    <>
      <button ref={button} type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 text-sm text-ink hover:bg-subtle">{label}</button>
      {open && createPortal(
        <div ref={menu} role="menu" dir={button.current ? getComputedStyle(button.current).direction : undefined}
          style={{ position: "fixed", top: pos?.top, bottom: pos?.bottom, left: pos?.left ?? -9999, width: Math.min(MENU_W, window.innerWidth - 2 * MARGIN),
            maxHeight: pos?.maxHeight, visibility: pos ? "visible" : "hidden" }}
          className="anim-pop z-[60] overflow-y-auto rounded-xl border border-line bg-white p-2 text-ink shadow-pop">
          {children(close)}
        </div>,
        document.body,
      )}
    </>
  );
}
